// src/components/Sections.jsx

import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, useScroll, useSpring, useTransform } from "motion/react";
import { Check } from "lucide-react";
import {
  HERO,
  EQUIPMENT_SECTION,
  WHY,
  CONTRACTS,
  AREAS,
  SITE,
  TAG,
} from "../content";
import { GlowCard, Magnetic, Tilt } from "./Effects";

// ---------------------------------------------------------------------------
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-70px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 18 }}
      animate={seen ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function Eyebrow({ children }) {
  return (
    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brooke-red">
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// The service tag a technician leaves on a unit after a visit. Cold Storage's
// hero is the data plate fitted at commissioning; this is its counterpart, and
// the pair keeps the two sites recognisably one estate without repeating the
// same object.
// ---------------------------------------------------------------------------
const TEMP_SEQ = [3, 2, 4, 3, 2, 3];

function ServiceTag() {
  const reduce = useReducedMotion();
  const [temp, setTemp] = useState(TEMP_SEQ[0]);

  useEffect(() => {
    if (reduce) return;
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % TEMP_SEQ.length;
      setTemp(TEMP_SEQ[i]);
    }, 2800);
    return () => clearInterval(t);
  }, [reduce]);

  return (
    <Tilt max={6}>
      <div className="bk-plate relative overflow-hidden rounded-xl border border-line shadow-[0_24px_60px_-28px_rgba(4,12,20,0.6)]">
        <div className="bk-sheen pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-[linear-gradient(100deg,transparent,rgb(var(--bk-surface)/0.8),transparent)]" />

        {/* punched hole and the eyelet, as a tag hangs on a unit */}
        <span className="absolute left-1/2 top-3.5 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-line bg-bg" />

        <div className="relative px-7 pb-8 pt-10 sm:px-9 sm:pb-10">
          <div className="flex items-start justify-between border-b border-line pb-5">
            <div>
              <p className="font-mono text-[8.5px] uppercase tracking-[0.24em] text-subtle">
                Brooke Refrigeration
              </p>
              <p className="mt-1 font-arch text-sm font-extrabold uppercase tracking-tight text-ink">
                Service Record
              </p>
            </div>
            <span className="flex items-center font-mono text-[8.5px] uppercase tracking-[0.18em] text-subtle">
              <i className="bk-dot mr-1.5 h-[5px] w-[5px] rounded-full bg-[#41D98A] shadow-[0_0_8px_#41D98A]" />
              In spec
            </span>
          </div>

          <div className="flex items-end justify-between py-7">
            <span className="font-mono text-[38px] font-bold leading-none tabular-nums tracking-tight text-led sm:text-[52px]">
              +{temp}
              <span className="ml-1 text-[0.34em] opacity-70">&deg;C</span>
            </span>
            <span className="pb-2 text-right font-mono text-[9px] uppercase leading-relaxed tracking-[0.18em] text-subtle">
              Reading
              <br />
              at last visit
            </span>
          </div>

          <dl className="border-t border-line">
            {TAG.map((row) => (
              <div
                key={row.label}
                className="flex items-baseline justify-between gap-6 border-b border-line py-3"
              >
                <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-subtle">
                  {row.label}
                </dt>
                <dd className="text-right font-mono text-[11.5px] tracking-[0.04em] text-muted">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </Tilt>
  );
}

// ---------------------------------------------------------------------------
export function Hero() {
  return (
    <section className="bk-wash relative overflow-hidden">
      <div className="bk-vignette pointer-events-none absolute inset-0" />
      <div className="bk-panel-lines pointer-events-none absolute inset-0 opacity-40" />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 overflow-hidden">
        <i className="bk-drift absolute -left-[5%] bottom-0 block h-full w-2/3 rounded-[50%] bg-[radial-gradient(ellipse_at_50%_70%,rgb(var(--bk-led)/0.18),transparent_68%)] blur-2xl" />
        <i
          className="bk-drift absolute bottom-0 left-[38%] block h-4/5 w-2/3 rounded-[50%] bg-[radial-gradient(ellipse_at_50%_70%,rgb(var(--bk-led)/0.14),transparent_68%)] blur-2xl"
          style={{ animationDelay: "-6s" }}
        />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-24 pt-32 sm:px-8 sm:pb-28 sm:pt-40 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
        <Reveal>
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-brooke-red" />
            <Eyebrow>{SITE.division}</Eyebrow>
          </div>

          <h1 className="mt-6 max-w-[15ch] font-arch text-[clamp(34px,6.5vw,64px)] font-extrabold leading-[1.02] tracking-[-0.03em] text-ink">
            {HERO.heading}
          </h1>

          <p className="mt-7 max-w-[52ch] text-[16px] leading-relaxed text-muted sm:text-[17px]">
            {HERO.body}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Magnetic>
              <a
                href={`tel:${SITE.phone}`}
                className="inline-flex min-h-[50px] items-center rounded-full bg-brooke-red px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white shadow-[0_10px_28px_-10px_rgba(231,0,26,0.8)] transition hover:bg-brooke-redDark"
              >
                Call {SITE.phoneLabel}
              </a>
            </Magnetic>
            <a
              href="#fault"
              className="inline-flex min-h-[50px] items-center rounded-full border border-line px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-muted transition hover:border-brooke-red hover:text-ink"
            >
              Report a fault
            </a>
          </div>

          <dl className="mt-14 flex flex-wrap gap-x-12 gap-y-6 border-t border-line pt-8">
            {HERO.stats.map((s) => (
              <div key={s.label}>
                <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-subtle">
                  {s.label}
                </dt>
                <dd className="mt-1.5 font-mono text-2xl font-bold tabular-nums text-led">
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>

        <Reveal delay={0.15}>
          <ServiceTag />
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
export function Equipment() {
  return (
    <section id="equipment" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
        <Reveal>
          <Eyebrow>{EQUIPMENT_SECTION.eyebrow}</Eyebrow>
          <h2 className="mt-3 max-w-[18ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            {EQUIPMENT_SECTION.heading}
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-4">
            {EQUIPMENT_SECTION.body.map((p, i) => (
              <p key={i} className="text-[15px] leading-relaxed text-muted sm:text-base">
                {p}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
            What we work on
          </p>
          <ul className="border-t border-line">
            {EQUIPMENT_SECTION.items.map((item) => (
              <li
                key={item}
                className="group flex items-center gap-3.5 border-b border-line py-3.5 text-[14.5px] text-muted transition-colors hover:text-ink"
              >
                <Check
                  size={14}
                  strokeWidth={2.5}
                  className="shrink-0 text-brooke-red"
                />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
export function Why() {
  return (
    <section id="why" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
            <div>
              <Eyebrow>{WHY.eyebrow}</Eyebrow>
              <h2 className="mt-3 max-w-[16ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
                {WHY.heading}
              </h2>
            </div>
            <p className="max-w-[58ch] self-end text-[15px] leading-relaxed text-muted sm:text-base">
              {WHY.body}
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.points.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.07} className="h-full">
              <GlowCard className="h-full rounded-2xl border border-line bg-surface2">
                <div className="h-full p-7">
                  <span className="font-mono text-[11px] font-bold tracking-[0.1em] text-brooke-red">
                    {p.n}
                  </span>
                  <h3 className="mt-4 font-arch text-lg font-extrabold leading-snug text-ink">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-muted">{p.body}</p>
                </div>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The rule fills as the section scrolls, the same device Cold Storage uses on
// its process steps.
// ---------------------------------------------------------------------------
export function Contracts() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 60%"],
  });
  const fill = useSpring(scrollYProgress, { stiffness: 90, damping: 30 });
  const height = useTransform(fill, [0, 1], ["0%", "100%"]);

  return (
    <section id="contracts" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>{CONTRACTS.eyebrow}</Eyebrow>
          <h2 className="mt-3 max-w-[16ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            {CONTRACTS.heading}
          </h2>
          <p className="mt-6 max-w-[62ch] text-[15px] leading-relaxed text-muted sm:text-base">
            {CONTRACTS.body}
          </p>
        </Reveal>

        <div ref={ref} className="relative mt-12 sm:pl-6">
          <div className="absolute left-0 top-0 hidden h-full w-px bg-line sm:block">
            <motion.div
              className="w-px bg-gradient-to-b from-brooke-red to-led"
              style={{ height }}
            />
          </div>

          <ul>
            {CONTRACTS.points.map((p, i) => (
              <Reveal key={p} delay={i * 0.06}>
                <li className="grid gap-4 border-t border-line py-6 sm:grid-cols-[60px_1fr] sm:gap-10">
                  <span className="border-l-2 border-brooke-red pl-3 font-mono text-[12px] font-bold tracking-[0.1em] text-subtle">
                    0{i + 1}
                  </span>
                  <p className="max-w-[64ch] text-[15px] leading-relaxed text-muted">
                    {p}
                  </p>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
export function Coverage() {
  return (
    <section id="coverage" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Coverage</Eyebrow>
          <h2 className="mt-3 font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            Where we work
          </h2>
          <p className="mt-6 max-w-[52ch] text-[15px] leading-relaxed text-muted">
            Emergency call-outs and scheduled maintenance across Pretoria and
            the surrounding areas, from the workshop in {SITE.address.locality}.
          </p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {AREAS.map((a) => (
              <li
                key={a}
                className="rounded-full border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-muted transition hover:border-brooke-red hover:text-ink"
              >
                {a}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}